// Typed engine stages follow ask-stage.v1; figures bind only to governed computations.
import { ASK_STAGE_SKIP_CODES, type AskStageReason } from './ask-stage.v1';
import type { AskHelixNumericBindingDto } from '../../ask-helix';
import { FIGURE_PERIOD_BASES, type FigurePeriodBasis } from '../../figure-period-basis';
import { readSynergyReconciliations } from './synergy-reconciliation';
import { readSynergyLoopRecovery, type SynergyLoopRecoveryProfile } from './synergy-loop-recovery.v1';
import { recordListsViewSchema, type RecordListsView } from './record-lists';
import { centsToDollarText, formatCompactMoney } from '../../compact-money';
import type { StoryFactPack, StoryRelationSegment } from '../../story-fact-pack';
export type { StoryFact, StoryFactPack, StoryRelation, StoryScope, StoryRelationSegment,
  StoryFailureCode, StoryRelationFailure, StoryRelationCheck } from '../../story-fact-pack';
import {
  readSynergyLoopDeliverableOutcome, readSynergyLoopLevers, type SynergyLoopDeliverableOutcome,
  type SynergyLoopLever, type SynergyLoopLeverOverride,
} from './synergy-loop-levers.v1';
export * from './synergy-loop-levers.v1';
import { readSummaryField, type SynergyLoopSummaryField } from './synergy-loop-summary.v1';
import { readSynergyLoopIdentity } from './synergy-loop-identity.v1';
export type { SynergyLoopRecoveryProfile } from './synergy-loop-recovery.v1';
export const SYNERGY_LOOP_SCHEMA_VERSION = 'synergy-loop.v1' as const;
/** Loop 1 over one synergy, in the only order the stages may be emitted. */
export const SYNERGY_LOOP_STAGES = [
  'status_owner', 'landing', 'causes', 'next_move', 'what_if', 'resolve', 'narrative',
] as const;
export type SynergyLoopStage = (typeof SYNERGY_LOOP_STAGES)[number];
export type SynergyLoopEngineStage = Exclude<SynergyLoopStage, 'narrative'>;
/** What the panel says while each stage runs. */
export const SYNERGY_LOOP_STAGE_LABELS: Readonly<Record<SynergyLoopStage, string>> = {
  status_owner: 'Checking status and owner',
  landing: 'Forecasting where it lands',
  causes: 'Testing causes',
  next_move: 'Choosing the next move',
  what_if: 'Pricing the levers',
  resolve: 'Checking what can still be delivered',
  narrative: 'Writing the answer',
};
export const SYNERGY_PANEL_QUESTION_LABELS: Readonly<Record<SynergyLoopStage, string>> = {
  status_owner: 'Is it on track?',
  landing: 'Where will it land?',
  causes: 'Why is it behind?',
  next_move: 'What should we do?',
  what_if: 'What should we do?',
  resolve: 'What should we do?',
  narrative: 'The verdict',
};
export const SYNERGY_PANEL_EXHIBIT_SUBLABELS: Readonly<Partial<Record<SynergyLoopEngineStage, string>>> = {
  what_if: 'What-if', resolve: 'Where it lands if we act',
};

export const SYNERGY_LOOP_WAITING_TEXT: Readonly<Record<SynergyLoopEngineStage, string>> = {
  status_owner: 'Status not yet published for this commitment.',
  landing: 'Forecast not yet published for this commitment.',
  causes: 'Causes not yet tested for this commitment.',
  next_move: 'Not yet measurable',
  what_if: 'Enter a run-rate below to see where this commitment would land.',
  resolve: 'What can still be delivered is not computed for this commitment yet.',
};
export const SYNERGY_LOOP_NOT_ANALYSED_PHRASE = {
  what_if: 'whether a recommended step would close the gap',
  resolve: 'what can still be delivered under constraints',
} as const;
/** One typed recommendation refusal shared by the commitment rail and loop panel. */
export const SYNERGY_LOOP_RECOMMENDATION_REFUSAL = {
  code: 'REFUSED', text: 'Two recommendations are current for this commitment.',
} as const;
export const SYNERGY_LOOP_CHART_TITLES = {
  delivery: 'Reported vs plan to date', landing: 'Year-end forecast vs commitment',
  finish: 'Finish date, {year}',
} as const;
export const SYNERGY_LOOP_PLOT_WORD = { forecast: 'Forecast' } as const;
export const SYNERGY_LOOP_UNITS = { money: 'Annual run-rate savings, $M' } as const;
export const SYNERGY_LOOP_WITHHELD_CODES = ['number', 'figure', 'date', 'voice', 'term'] as const;
export type SynergyLoopWithheldCode = (typeof SYNERGY_LOOP_WITHHELD_CODES)[number];
export const SYNERGY_LOOP_WITHHELD_WORDS = {
  number: { one: "its figure doesn't match Helix's {Mon D} numbers",
    many: "their figures don't match Helix's {Mon D} numbers" },
  figure: { one: 'it cites a figure Helix has not computed',
    many: 'they cite figures Helix has not computed' },
  date: { one: "it gives a date Helix's records don't show",
    many: "they give dates Helix's records don't show" },
  voice: { one: 'it sounded more certain than the forecast supports',
    many: 'they sounded more certain than the forecast supports' },
  term: null,
  unknown: { one: "it could not be checked against Helix's figures",
    many: "they could not be checked against Helix's figures" },
} as const;

export const SYNERGY_LOOP_NOT_YET_COMPUTED = "Not yet computed";

export const SYNERGY_LOOP_STATUSES = ['started', 'completed', 'skipped'] as const;
export type SynergyLoopStatus = (typeof SYNERGY_LOOP_STATUSES)[number];

/** Proven = governed base fact; modeled = engine model; scenario = sandbox, never canonical. */
export const SYNERGY_LOOP_VOICES = ['proven', 'modeled', 'scenario'] as const;
export type SynergyLoopVoice = (typeof SYNERGY_LOOP_VOICES)[number];

/** The ONE place a voice comes from: the figure's result class (and the sandbox flag). */
export function synergyLoopVoiceOf(resultClass: string, scenario: boolean): SynergyLoopVoice {
  if (scenario) return 'scenario';
  return resultClass === 'base' || resultClass === 'observation' ? 'proven' : 'modeled';
}

/** Native bindings carry one value and one server display, as on /ask. */
export function synergyLoopBinding(input: {
  key: string; pointer: string; cents: bigint; scenario: boolean;
  label?: string; findingId?: string; measureId?: string; resultClass?: string;
  millionDigits?: 1 | 2;
  parts?: readonly Readonly<{ key: string; label: string; cents: bigint }>[];
}): AskHelixNumericBindingDto {
  const exactValue = centsToDollarText(input.cents);
  const voice = synergyLoopVoiceOf(input.resultClass ?? 'base', input.scenario);
  const kind = input.scenario ? 'scenario_option' : 'decision_agent_evidence';
  const valuePath = input.scenario && !input.pointer.startsWith('scenario:')
    ? `scenario:${input.pointer}` : input.pointer;
  const display = formatCompactMoney(exactValue, input.millionDigits ?? 2);
  return {
    placeholder: input.key, valuePath, value: Number(exactValue), exactValue,
    displayValue: display, displayValueCompact: display,
    displayLabel: input.label ?? input.key, definition: input.measureId ?? input.key,
    format: 'usd', unit: 'USD', claimDomain: 'finance_budget', isPartial: false,
    evidence: { kind, id: input.findingId ?? input.pointer },
    scope: { label: '' }, timeframe: { kind: 'as_of', label: '' },
    source: { label: '', surface: 'decision_agent_module', evidenceKind: kind, asOf: null },
    provenance: { schemaVersion: 'ask_helix_figure_provenance_v1',
      badge: voice === 'proven' ? 'approved' : 'modeled',
      glyph: voice === 'proven' ? '✓' : '~', label: voice, reasons: [] },
    reconciliation: input.parts
      ? { method: 'sum_of_parts',
          rule: 'board total = sum of the listed board-approved commitments',
          total: Number(exactValue), exactTotal: exactValue,
          components: input.parts.map((part) => {
            const text = centsToDollarText(part.cents);
            return { key: part.key, label: part.label, value: Number(text), exactValue: text };
          }) }
      : { method: 'direct_observation', rule: 'one published engine value' },
  };
}

/** Optional voice presentation, derived from the native binding's own provenance. */
export function synergyLoopVoice(binding: AskHelixNumericBindingDto): SynergyLoopVoice {
  if (binding.evidence?.kind === 'scenario_option') return 'scenario';
  return binding.provenance?.badge === 'approved' ? 'proven' : 'modeled';
}

export function synergyLoopRekey(
  binding: AskHelixNumericBindingDto, placeholder: string, displayLabel: string,
): AskHelixNumericBindingDto {
  return { ...binding, placeholder, displayLabel };
}

export const SYNERGY_LOOP_MEASURES = [
  'committed', 'plan_to_date', 'reported', 'behind_plan', 'ledger_verified',
  'reported_unbacked', 'year_end_forecast', 'year_end_shortfall', 'finish_slip_days',
  'dependency_days_late', 'scenario_before', 'scenario_after', 'scenario_change',
  'resolved_member', 'resolved_change', 'scenario_input', 'scenario_target', 'lever_count',
] as const;
export type SynergyLoopMeasure = (typeof SYNERGY_LOOP_MEASURES)[number];
export const SYNERGY_LOOP_MEASURE_LABELS: Readonly<Record<SynergyLoopMeasure, string>> = {
  committed: 'Committed run-rate', plan_to_date: 'Plan to date', reported: 'Reported',
  behind_plan: 'Behind plan', ledger_verified: 'Accounts-verified',
  reported_unbacked: 'Reported, not yet in the accounts', year_end_forecast: 'Year-end forecast',
  year_end_shortfall: 'Expected shortfall at year end', finish_slip_days: 'Finish delay',
  dependency_days_late: 'Late', scenario_before: 'Year-end forecast',
  scenario_after: 'With the what-if', scenario_change: 'Change',
  resolved_member: 'Deliverable by year end', resolved_change: 'Change from the forecast',
  scenario_input: 'Entered run-rate', scenario_target: 'Case target', lever_count: 'Count',
};
export type SynergyLoopBasis = Readonly<{
  periodBasis?: FigurePeriodBasis;
  measure: SynergyLoopMeasure; label?: string; period?: string; direction?: 'ahead' | 'behind';
  /** Words after the figure on its tile, e.g. its typed condition count ("if both … hold"). */
  qualifier?: string;
  /** The noun phrase for a conditional amount in prose, distinct from its tile caption. */
  storyLabel?: string;
}>;
export type SynergyLoopGeometry = Readonly<{
  bullet?: Readonly<{ delivered: number; plan: number; verified?: number }>;
  forecast?: Readonly<{ forecast: number; committed: number; withWhatIf?: number }>;
  slip?: Readonly<{
    asOfPosition?: number; asOfLabel?: string;
    target: number; forecast: number; ticks: readonly Readonly<{ label: string; at: number }>[];
  }>;
}>;
export const SYNERGY_LOOP_FACT_KINDS = [
  'status', 'owner', 'dependency', 'dependency_owner', 'finish_committed',
  'finish_forecast', 'recommendation', 'recommendation_why', 'cause', 'option',
  'lever_condition', 'lever_gate', 'lever_included', 'gate_forecast',
  /** Exhibit 6: the day the role exits finish at the exit pace the data shows (#10230). */
  'exit_pace_finish',
] as const;
export type SynergyLoopFactKind = (typeof SYNERGY_LOOP_FACT_KINDS)[number];
/** A worded engine fact, with optional typed presentation identity. */
export type SynergyLoopFact = Readonly<{
  key: string; label: string; text: string; kind?: SynergyLoopFactKind;
  scopeKind?: 'synergy_deal';
}>;

/** One narrative piece: text, or a figure referenced by key (rendered from its binding). */
export type SynergyLoopSegment =
  | Readonly<{ kind: 'text'; text: string; emphasis?: true; note?: SynergyLoopWithheldCode }>
  | Readonly<{ kind: 'figure'; key: string; emphasis?: true }>
  | StoryRelationSegment
  /** A model sentence the fence could not verify, replaced in place (never dropped silently). */
  | Readonly<{ kind: 'unverified'; reason: string; code?: SynergyLoopWithheldCode }>;

export type SynergyLoopNarrative = SynergyLoopSummaryField & Readonly<{
  source: 'model' | 'composer';
  /** Display-ready output from the shared governed compiler. */
  answerMarkdown?: string;
  paragraphs: readonly (readonly SynergyLoopSegment[])[];
  verdict?: readonly SynergyLoopSegment[];
  notes?: Readonly<Partial<Record<SynergyLoopEngineStage, readonly SynergyLoopSegment[]>>>;
  /** Why the AI summary could not be produced: ONE fixed server-authored line. */
  unavailable?: SynergyLoopNarrativeUnavailable;
}>;

export const SYNERGY_LOOP_UNAVAILABLE_CODES = [
  'daily_ai_budget_used',
  'model_not_configured',
  'model_failed',
  'nothing_verified',
] as const;

export type SynergyLoopUnavailableCode = (typeof SYNERGY_LOOP_UNAVAILABLE_CODES)[number];

export type SynergyLoopNarrativeUnavailable =
  | Readonly<{ code: SynergyLoopUnavailableCode; text: string }>
  | Readonly<{ code: 'unknown' }>;

export type SynergyLoopRevision = Readonly<{
  datasetRef: string; revisionId: string; version: number;
}>;

/** Names both revisions when status and landing were computed from different cuts. */
export const SYNERGY_LOOP_SOURCES_OUT_OF_STEP = 'landing.sources_out_of_step' as const;

export type SynergyLoopStageResult = Readonly<{
  recordLists?: RecordListsView;
  investigation?: import('./saved-investigation').ObjectiveInvestigationHandoff;
  actionPlan?: import('../views/saved-action-plan').SavedActionPlanView;
  causesTitle?: string;
  recoveryOptions?: readonly SynergyLoopRecoveryProfile[];
  reconciliations?: readonly import('./synergy-reconciliation').SynergyReconciliation[];
  findingIds: readonly string[];
  facts: readonly SynergyLoopFact[];
  figures: readonly AskHelixNumericBindingDto[];
  narrative?: SynergyLoopNarrative;
  headline?: readonly SynergyLoopSegment[];
  figureBasis?: Readonly<Record<string, SynergyLoopBasis>>;
  geometry?: SynergyLoopGeometry;
  sourceNote?: string;
  notes?: readonly SynergyLoopSegment[];
  /** The landing-forecast revision this stage's figures rest on, when it reads one. */
  landingRevision?: SynergyLoopRevision;
  /** Priced levers in rank order (what-if stage); each names its figure by key. */
  levers?: readonly SynergyLoopLever[];
  /** What can still be delivered, typed (resolve stage from levers). */
  leverOutcome?: SynergyLoopDeliverableOutcome;
  /** A figure tile as labelled lines of words and figure tokens (resolve stage's deliverable). */
  tile?: readonly (readonly SynergyLoopSegment[])[];
  /** Engine-declared facts and relations for the existing narrative/repair seam. */
  factPack?: StoryFactPack;
}>;

export type SynergyLoopSubject = Readonly<{
  dealRef: string;
  commitmentRef: string;
  asOf: string;
  label: string | null;
}>;

export type SynergyLoopStartFrame = Readonly<{
  schemaVersion: typeof SYNERGY_LOOP_SCHEMA_VERSION;
  kind: 'loop.start';
  subject: SynergyLoopSubject;
  scenario: boolean;
}>;

export type SynergyLoopStageFrame = Readonly<{
  schemaVersion: typeof SYNERGY_LOOP_SCHEMA_VERSION;
  kind: 'loop.stage';
  seq: number;
  stage: SynergyLoopStage;
}> &
  (
    | Readonly<{ status: 'started'; reason: null; result: null }>
    | Readonly<{ status: 'completed'; reason: null; result: SynergyLoopStageResult }>
    | Readonly<{ status: 'skipped'; reason: AskStageReason; result: null }>
  );

export type SynergyLoopFrame = SynergyLoopStartFrame | SynergyLoopStageFrame;

/** Sandbox input: in-memory only, never written. A run-rate, or an override on one lever. */
export const SYNERGY_LOOP_OVERRIDE_KINDS = ['member_landing', 'lever_date', 'lever_count'] as const;
export type SynergyLoopRunRateOverride = Readonly<{
  kind: 'member_landing';
  commitmentRef: string;
  /** The realized run-rate the scenario sets for this synergy, in whole cents. */
  valueCents: string;
}>;
export type SynergyLoopOverride = SynergyLoopRunRateOverride | SynergyLoopLeverOverride;
export const isRunRateOverride = (override: SynergyLoopOverride):
  override is SynergyLoopRunRateOverride => override.kind === 'member_landing';
export type SynergyLoopRequest = Readonly<{
  dealRef: string;
  commitmentRef: string;
  asOf: string;
  overrides?: readonly SynergyLoopOverride[];
  narration?: 'requested';
}>;

export const SYNERGY_LOOP_PATH = 'v1/views/synergy-loop';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown): value is string => typeof value === 'string' && value !== '';

/** Read the native binding without admitting its value; the shared fence owns that. */
export function readSynergyLoopFigure(value: unknown): AskHelixNumericBindingDto | null {
  if (!isRecord(value) || !isText(value.placeholder) || !isText(value.displayLabel)) return null;
  return value as unknown as AskHelixNumericBindingDto;
}

function readFact(value: unknown): SynergyLoopFact | null {
  if (!isRecord(value) || !isText(value.key) || !isText(value.label) || !isText(value.text))
    return null;
  const kind = readEnum(SYNERGY_LOOP_FACT_KINDS, value.kind);
  return { key: value.key, label: value.label, text: value.text, ...(kind ? { kind } : {}) };
}

function readEnum<T extends string>(values: readonly T[], value: unknown): T | undefined {
  return values.find((entry) => entry === value);
}

function readSegment(value: unknown): SynergyLoopSegment | null {
  if (!isRecord(value)) return null;
  const emphasis = value.emphasis === true ? { emphasis: true as const } : {};
  if (value.kind === 'text' && isText(value.text)) {
    const note = readEnum(SYNERGY_LOOP_WITHHELD_CODES, value.note);
    return { kind: 'text', text: value.text, ...emphasis, ...(note ? { note } : {}) };
  }
  if (value.kind === 'figure' && isText(value.key))
    return { kind: 'figure', key: value.key, ...emphasis };
  if (value.kind === 'relation' && isText(value.key) && isText(value.text))
    return { kind: 'relation', key: value.key, text: value.text };
  if (value.kind === 'unverified' && isText(value.reason)) {
    const code = readEnum(SYNERGY_LOOP_WITHHELD_CODES, value.code);
    return { kind: 'unverified', reason: value.reason, ...(code ? { code } : {}) };
  }
  return null;
}

export const readSynergyLoopSegment = readSegment; // the Overview summary reads segments

function readAll<T>(value: unknown, read: (item: unknown) => T | null): T[] | null {
  if (!Array.isArray(value)) return null;
  const out: T[] = [];
  for (const item of value) {
    const next = read(item);
    if (next === null) return null;
    out.push(next);
  }
  return out;
}

function readNarrative(value: unknown): SynergyLoopNarrative | null {
  if (!isRecord(value) || (value.source !== 'model' && value.source !== 'composer')) return null;
  const answerMarkdown = isText(value.answerMarkdown) ? value.answerMarkdown : undefined;
  const paragraphs = readAll(value.paragraphs, (item) => readAll(item, readSegment));
  if (paragraphs === null && answerMarkdown === undefined) return null;
  const verdict = readSegments(value.verdict);
  const notes = readNotes(value.notes);
  const unavailable = readUnavailable(value.unavailable);
  return { source: value.source, paragraphs: paragraphs ?? [],
    ...(answerMarkdown === undefined ? {} : { answerMarkdown }), ...readSummaryField(value.summary),
    ...(verdict ? { verdict } : {}), ...(notes ? { notes } : {}),
    ...(unavailable ? { unavailable } : {}) };
}

function readUnavailable(value: unknown): SynergyLoopNarrativeUnavailable | null {
  if (value === undefined) return null;
  const code = isRecord(value) ? readEnum(SYNERGY_LOOP_UNAVAILABLE_CODES, value.code) : undefined;
  const text = isRecord(value) && typeof value.text === 'string' ? value.text : '';
  return code && text.trim() !== '' ? { code, text } : { code: 'unknown' };
}

/** New presentation fields are optional; invalid additions never invalidate legacy content. */
function readSegments(value: unknown): SynergyLoopSegment[] | null {
  if (!Array.isArray(value)) return null;
  return readAll(value.filter((item) => !isRecord(item) || item.kind === 'text'
    || item.kind === 'figure' || item.kind === 'relation' || item.kind === 'unverified'),
  readSegment);
}

function readNotes(value: unknown): SynergyLoopNarrative['notes'] {
  if (!isRecord(value)) return undefined;
  return Object.fromEntries(SYNERGY_LOOP_STAGES.flatMap((stage) => {
    if (stage === 'narrative') return [];
    const segments = readSegments(value[stage]);
    return segments ? [[stage, segments]] : [];
  }));
}

function readFigureBasis(value: unknown): SynergyLoopStageResult['figureBasis'] {
  if (!isRecord(value)) return undefined;
  return Object.fromEntries(Object.entries(value).flatMap(([key, entry]) => {
    if (!isRecord(entry)) return [];
    const measure = readEnum(SYNERGY_LOOP_MEASURES, entry.measure);
    if (!measure) return [];
    const period = isText(entry.period) ? { period: entry.period } : {};
    const periodBasis = readEnum(FIGURE_PERIOD_BASES, entry.periodBasis);
    const direction = readEnum(['ahead', 'behind'] as const, entry.direction);
    const label = isText(entry.label) ? { label: entry.label } : {};
    const qualifier = isText(entry.qualifier) ? { qualifier: entry.qualifier } : {};
    return [[key, { measure, ...label, ...period, ...(periodBasis ? { periodBasis } : {}), ...(direction ? { direction } : {}),
      ...qualifier }]];
  }));
}

const isRatio = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;

function readGeometry(value: unknown): SynergyLoopGeometry | undefined {
  if (!isRecord(value)) return undefined;
  const { bullet, forecast } = value;
  const slip = readSlip(value.slip);
  return {
    ...(isRecord(bullet) && isRatio(bullet.delivered) && isRatio(bullet.plan)
      ? { bullet: { delivered: bullet.delivered, plan: bullet.plan,
        ...(isRatio(bullet.verified) && bullet.verified <= bullet.delivered
          ? { verified: bullet.verified } : {}) } } : {}),
    ...(isRecord(forecast) && isRatio(forecast.forecast) && isRatio(forecast.committed)
      ? { forecast: { forecast: forecast.forecast, committed: forecast.committed,
        ...(isRatio(forecast.withWhatIf) ? { withWhatIf: forecast.withWhatIf } : {}) } } : {}),
    ...(slip ? { slip } : {}),
  };
}

function readSlip(value: unknown): SynergyLoopGeometry['slip'] {
  if (!isRecord(value) || !isRatio(value.target) || !isRatio(value.forecast)) return undefined;
  const ticks = readAll(value.ticks, (tick) =>
    isRecord(tick) && isText(tick.label) && isRatio(tick.at)
      ? { label: tick.label, at: tick.at } : null);
  return ticks ? { target: value.target, forecast: value.forecast, ticks,
    ...(isRatio(value.asOfPosition) && isText(value.asOfLabel)
      ? { asOfPosition: value.asOfPosition, asOfLabel: value.asOfLabel } : {}) } : undefined;
}

function readAdditions(value: Record<string, unknown>): Partial<SynergyLoopStageResult> {
  const recordLists = recordListsViewSchema.safeParse(value.recordLists);
  const headline = readSegments(value.headline);
  const notes = readSegments(value.notes);
  const figureBasis = readFigureBasis(value.figureBasis);
  const geometry = readGeometry(value.geometry);
  const levers = readSynergyLoopLevers(value.levers);
  const leverOutcome = readSynergyLoopDeliverableOutcome(value.leverOutcome);
  const reconciliations = readSynergyReconciliations(value.reconciliations);
  const recoveryOptions = readSynergyLoopRecovery(value.recoveryOptions);
  const tile = Array.isArray(value.tile) ? readAll(value.tile, readSegments) : null;
  return {
    ...(recordLists.success ? { recordLists: recordLists.data } : {}),
    ...(reconciliations.length ? { reconciliations } : {}),
    ...(recoveryOptions ? { recoveryOptions } : {}),
    ...(levers ? { levers } : {}), ...(leverOutcome ? { leverOutcome } : {}),
    ...(tile && tile.length > 0 ? { tile } : {}),
    ...(headline ? { headline } : {}), ...(notes ? { notes } : {}),
    ...(figureBasis ? { figureBasis } : {}), ...(geometry ? { geometry } : {}),
    ...(isText(value.sourceNote) ? { sourceNote: value.sourceNote } : {}),
    ...(isText(value.causesTitle) ? { causesTitle: value.causesTitle } : {}),
  };
}

export function readSynergyLoopResult(value: unknown): SynergyLoopStageResult | null {
  if (!isRecord(value)) return null;
  const actionPlan = readSavedActionPlanAddition(value.actionPlan);
  if (actionPlan === null) return null;
  const findingIds = readAll(value.findingIds, (item) => (isText(item) ? item : null));
  const facts = readAll(value.facts, readFact);
  const figures = readAll(value.figures, readSynergyLoopFigure);
  if (findingIds === null || facts === null || figures === null) return null;
  const keys = figures.map((figure) => figure.placeholder);
  if (new Set(keys).size !== keys.length) return null;
  const identity = readSynergyLoopIdentity(value);
  if (identity === null) return null;
  const result = { findingIds, facts, figures, ...readAdditions(value),
    ...actionPlan, ...identity };
  if (value.narrative === undefined) return result;
  const narrative = readNarrative(value.narrative);
  return narrative === null ? null : { ...result, narrative };
}

function readReason(value: unknown): AskStageReason | null {
  if (!isRecord(value) || !isText(value.text)) return null;
  const code = value.code as AskStageReason['code'];
  return ASK_STAGE_SKIP_CODES.includes(code) ? { code, text: value.text } : null;
}

/** Strict reader for one NDJSON line; anything off-contract is dropped (null), never guessed. */
export function readSynergyLoopFrame(value: unknown): SynergyLoopFrame | null {
  if (!isRecord(value) || value.schemaVersion !== SYNERGY_LOOP_SCHEMA_VERSION) return null;
  if (value.kind === 'loop.start') {
    const subject = value.subject;
    if (!isRecord(subject) || typeof value.scenario !== 'boolean') return null;
    const { dealRef, commitmentRef, asOf, label } = subject;
    if (!isText(dealRef) || !isText(commitmentRef) || !isText(asOf)) return null;
    if (label !== null && !isText(label)) return null;
    return {
      schemaVersion: SYNERGY_LOOP_SCHEMA_VERSION,
      kind: 'loop.start',
      subject: { dealRef, commitmentRef, asOf, label: label as string | null },
      scenario: value.scenario,
    };
  }
  if (value.kind !== 'loop.stage') return null;
  const { seq, stage, status } = value;
  if (typeof seq !== 'number' || !Number.isInteger(seq) || seq < 0) return null;
  if (!SYNERGY_LOOP_STAGES.includes(stage as SynergyLoopStage)) return null;
  const base = {
    schemaVersion: SYNERGY_LOOP_SCHEMA_VERSION,
    kind: 'loop.stage' as const,
    seq,
    stage: stage as SynergyLoopStage,
  };
  if (status === 'started')
    return value.reason === null && value.result === null
      ? { ...base, status, reason: null, result: null }
      : null;
  if (status === 'completed') {
    const result = value.reason === null ? readSynergyLoopResult(value.result) : null;
    return result === null ? null : { ...base, status, reason: null, result };
  }
  if (status === 'skipped') {
    const reason = value.result === null ? readReason(value.reason) : null;
    return reason === null ? null : { ...base, status, reason, result: null };
  }
  return null;
}

/** One stage as the panel draws it. `pending` = not reached yet. */
export type SynergyLoopStageView =
  | Readonly<{ status: 'pending' }>
  | Readonly<{ status: 'started' }>
  | Readonly<{ status: 'completed'; result: SynergyLoopStageResult }>
  | Readonly<{ status: 'skipped'; reason: AskStageReason }>;

export type SynergyLoopState = Readonly<{
  subject: SynergyLoopSubject | null;
  scenario: boolean;
  lastSeq: number;
  stages: Readonly<Record<SynergyLoopStage, SynergyLoopStageView>>;
}>;

export function emptySynergyLoopState(): SynergyLoopState {
  const stages = Object.fromEntries(
    SYNERGY_LOOP_STAGES.map((stage) => [stage, { status: 'pending' }]),
  ) as Record<SynergyLoopStage, SynergyLoopStageView>;
  return { subject: null, scenario: false, lastSeq: -1, stages };
}

const reached = (view: SynergyLoopStageView) => view.status !== 'pending';

/**
 * Forward-only: an older seq, a stage before one already reached, a restart, or a finish
 * without a start (other than a direct skip) changes nothing. Nothing is inferred.
 */
export function reduceSynergyLoop(
  state: SynergyLoopState,
  frame: SynergyLoopFrame,
): SynergyLoopState {
  if (frame.kind === 'loop.start')
    return { ...emptySynergyLoopState(), subject: frame.subject, scenario: frame.scenario };
  if (frame.seq <= state.lastSeq) return state;
  const index = SYNERGY_LOOP_STAGES.indexOf(frame.stage);
  const current = state.stages[frame.stage];
  const later = SYNERGY_LOOP_STAGES.slice(index + 1).some((stage) =>
    reached(state.stages[stage]),
  );
  let next: SynergyLoopStageView;
  if (frame.status === 'started') {
    if (current.status !== 'pending' || later) return state;
    next = { status: 'started' };
  } else if (frame.status === 'completed') {
    if (current.status !== 'started') return state;
    next = { status: 'completed', result: frame.result };
  } else {
    if (current.status !== 'pending' && current.status !== 'started') return state;
    if (current.status === 'pending' && later) return state;
    next = { status: 'skipped', reason: frame.reason };
  }
  return {
    ...state,
    lastSeq: frame.seq,
    stages: { ...state.stages, [frame.stage]: next },
  };
}
import { readSavedActionPlanAddition } from '../views/saved-action-plan';
